import {
  CarcassonneModuleId,
  ScoringRule,
  ScoringTableRow,
  ScoringTimingKind,
} from '../types';
import { ALL_MODULE_IDS, getModuleById } from './modules';

const GAME_ID = 'carcassonne' as const;

/**
 * Shared scoring catalogue. Filter with `buildScoringTable` for the active
 * module set — do not maintain separate per-expansion scoring tables in UI.
 *
 * Component-dependent values stay marked as such; never invent printed numbers.
 */
export const carcassonneScoringRules: ScoringRule[] = [
  // ——— Base ———
  {
    id: 'base-majority',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Większość i remisy',
    whenScores: 'Przy każdym punktowaniu obszaru z figurami graczy.',
    scoring:
      'Gracz z największą łączną siłą otrzymuje pełną wartość obszaru. Remis większości = pełne punkty dla każdego remisującego (nie dzieli się).',
    conditions:
      'Zwykły meeple ma siłę 1. Siłę sumuje się osobno dla każdego gracza na danym obszarze.',
    exceptions:
      'Nie liczy się, kto zaczął obszar ani kto dołożył zamykający kafelek. Rozszerzenia mogą zmieniać siłę figur (np. duży meeple = 2).',
    majorityRule:
      'Największa siła wygrywa; remis = pełne punkty dla każdego remisującego.',
    returnsMeeple: 'Tak — po przyznaniu punktów z ukończonego obszaru (chłopi: zwykle dopiero na końcu).',
    moduleId: 'base',
    category: 'base',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 5,
    keywords: ['większość', 'wiekszosc', 'remis', 'majority'],
    exactPointValues: [],
  },
  {
    id: 'base-road',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Droga',
    whenScores:
      'W trakcie gry po ukończeniu (oba końce zamknięte lub pętla). Na końcu gry także nieukończone drogi.',
    scoring:
      '1 punkt za każdy kafelek drogi (ukończona i nieukończona tak samo).',
    conditions:
      'Policz każdy kafelek drogi raz — nawet jeśli ma kilka odcinków tej samej drogi.',
    exceptions:
      'Aktywne dodatki mogą zmienić wartość (karczma, most, Mag/Wiedźma).',
    majorityRule: 'Jak baza: remis = pełne punkty dla każdego remisującego.',
    returnsMeeple:
      'Tak — po punktowaniu ukończonej drogi. Na końcu: po punktowaniu nieukończonej drogi.',
    moduleId: 'base',
    category: 'base',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 10,
    keywords: ['droga', 'podróżnik', 'podroznik'],
    exactPointValues: ['1'],
  },
  {
    id: 'base-city',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Miasto',
    whenScores:
      'W trakcie gry po ukończeniu (brak otwartej krawędzi miasta). Na końcu gry także nieukończone miasta.',
    scoring:
      'Ukończone: 2 punkty za kafelek + 2 za każdy herb. Nieukończone na końcu: 1 za kafelek + 1 za herb.',
    conditions: 'Każdy kafelek miasta i każdy herb liczy się raz w danym mieście.',
    exceptions:
      'Katedra (Karczmy i Katedry) zmienia wartości; Mag/Wiedźma modyfikują wynik.',
    majorityRule: 'Jak baza: remis = pełne punkty dla każdego remisującego.',
    returnsMeeple:
      'Tak — po punktowaniu ukończonego miasta. Na końcu: po punktowaniu nieukończonego.',
    moduleId: 'base',
    category: 'base',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 20,
    keywords: ['miasto', 'herb', 'rycerz'],
    exactPointValues: ['2', '1'],
  },
  {
    id: 'base-monastery',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Klasztor',
    whenScores:
      'W trakcie gry gdy klasztor ma 8 sąsiadów (także po przekątnych). Na końcu gry także nieukończone klasztory.',
    scoring:
      'Ukończony: 9 punktów. Nieukończony na końcu: 1 punkt + 1 za każdego obecnego sąsiada.',
    conditions: 'Sąsiedzi = 8 pól wokół (ortogonalnie i ukośnie).',
    exceptions:
      'Ogród (Opat) punktuje jak klasztor. Winnica dodaje premię przy punktowaniu.',
    majorityRule: 'Jak baza: remis = pełne punkty dla każdego remisującego.',
    returnsMeeple:
      'Tak — po punktowaniu ukończonego klasztoru. Na końcu: po punktowaniu nieukończonego.',
    moduleId: 'base',
    category: 'base',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 30,
    keywords: ['klasztor', 'mnich'],
    exactPointValues: ['9', '1'],
  },
  {
    id: 'base-field',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Pole',
    whenScores: 'Wyłącznie na końcu gry (chłopi zwykle zostają do końca).',
    scoring:
      '3 punkty za każde ukończone miasto stykające się z polem. W jednym polu dane miasto liczy się raz; to samo miasto może punktować dla kilku pól.',
    conditions:
      'Najpierw rozstrzygnij większość na polu; dopiero potem licz miasta. Pole nigdy nie „kończy się” w trakcie partii.',
    exceptions:
      'Dodatki mogą mieć własne triggery na polu (świnia, strach, pasterz, stodoła) — nie mylić z końcowym 3/miasto.',
    majorityRule: 'Jak baza: remis = pełne punkty dla każdego remisującego.',
    returnsMeeple:
      'Nie w trakcie gry — chłop zwykle wraca dopiero po punktowaniu końcowym pola.',
    moduleId: 'base',
    category: 'base',
    timing: 'end-game',
    sourceStatus: 'verified',
    verified: true,
    order: 40,
    keywords: ['pole', 'chłop', 'chlop', 'łąka', 'laka'],
    exactPointValues: ['3'],
  },

  // ——— Rzeka ———
  {
    id: 'river-no-scoring',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Rzeka',
    whenScores: 'Nie punktuje samodzielnie.',
    scoring:
      '0 — rzeka nie jest punktowanym obszarem. Punkty dają wyłącznie drogi, miasta, klasztory i pola narysowane na jej kafelkach według zasad podstawy.',
    conditions: 'Rzeka zastępuje kafelek startowy i buduje wspólny początek krajobrazu.',
    exceptions: 'Nie traktuj biegu wody jak drogi do punktowania.',
    majorityRule: 'N/A — rzeka nie ma własnej większości.',
    returnsMeeple: 'N/A — nie stawia się figury „na wodzie”.',
    moduleId: 'river',
    category: 'core',
    timing: 'none',
    sourceStatus: 'no-scoring-impact',
    verified: true,
    order: 45,
    keywords: ['rzeka', 'river'],
    exactPointValues: ['0'],
  },

  // ——— Opat ———
  {
    id: 'abbot',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Opat',
    whenScores:
      'W trakcie gry: zamiast stawiania figury możesz wycofać opata z nieukończonego klasztoru lub ogrodu. Na końcu: jak klasztor/ogród.',
    scoring:
      'Przy wcześniejszym wycofaniu: 1 punkt za kafelek klasztoru/ogrodu + 1 za każdego obecnego sąsiada. Ukończony ogród/klasztor z opatem: 9 punktów.',
    conditions: 'Opat tylko na klasztorze lub ogrodzie. Siła 1.',
    exceptions:
      'Nie można jednocześnie postawić figury i wycofać opata. Opat nie zastępuje rycerza, podróżnika ani chłopa.',
    majorityRule: 'Siła 1 — jak zwykły meeple na klasztorze/ogrodzie.',
    returnsMeeple:
      'Tak — po wcześniejszym wycofaniu z punktami albo po punktowaniu ukończenia.',
    moduleId: 'abbot',
    category: 'core',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 50,
    keywords: ['opat', 'ogród', 'ogrod'],
    exactPointValues: ['1', '9'],
  },

  // ——— Major 01: Karczmy / Katedry ———
  {
    id: 'm01-inn-mod',
    gameId: GAME_ID,
    kind: 'modifier',
    element: 'Karczma',
    whenScores: '',
    scoring: '',
    conditions: '',
    exceptions:
      'Karczma: ukończona droga 2/kafelek; nieukończona (C3.1) 1/kafelek — nie 0. Kilka karczm na drodze nie mnoży.',
    majorityRule: '',
    returnsMeeple: '',
    moduleId: 'major-01-inns-cathedrals',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    attachesToRuleId: 'base-road',
    order: 110,
    keywords: ['karczma'],
    exactPointValues: ['2', '1'],
  },
  {
    id: 'm01-inn',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Karczma',
    whenScores: 'Przy punktowaniu drogi zawierającej kafelek z karczmą.',
    scoring:
      'Ukończona droga z karczmą: 2 punkty/kafelek. Nieukończona na końcu (C3.1): 1 punkt/kafelek (nie zero ze starych wydań).',
    conditions: 'Wystarczy jedna karczma na drodze.',
    exceptions: 'Kilka karczm na tej samej drodze nie mnoży wyniku.',
    majorityRule: 'Jak droga podstawowa.',
    returnsMeeple: 'Jak droga — po scoringu wraca podróżnik.',
    moduleId: 'major-01-inns-cathedrals',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 111,
    keywords: ['karczma', 'inns'],
    exactPointValues: ['2', '1'],
  },
  {
    id: 'm01-cathedral-mod',
    gameId: GAME_ID,
    kind: 'modifier',
    element: 'Katedra',
    whenScores: '',
    scoring: '',
    conditions: '',
    exceptions:
      'Katedra: ukończone miasto 3/kafelek + 3/herb; nieukończone na końcu 1+1 (nie zero).',
    majorityRule: '',
    returnsMeeple: '',
    moduleId: 'major-01-inns-cathedrals',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    attachesToRuleId: 'base-city',
    order: 120,
    keywords: ['katedra'],
    exactPointValues: ['3', '1'],
  },
  {
    id: 'm01-cathedral',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Katedra',
    whenScores: 'Przy punktowaniu miasta zawierającego katedrę.',
    scoring:
      'Ukończone: 3 punkty/kafelek + 3/herb. Nieukończone na końcu: 1/kafelek + 1/herb (C3.1 — nie zero).',
    conditions: 'Katedra musi należeć do punktowanego miasta.',
    exceptions: 'Duży meeple ma siłę 2 przy większości; nie podwaja punktów za kafelki.',
    majorityRule: 'Duży meeple = siła 2; remis nadal = pełne punkty.',
    returnsMeeple: 'Jak miasto — po scoringu wracają rycerze (w tym duży meeple).',
    moduleId: 'major-01-inns-cathedrals',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 121,
    keywords: ['katedra', 'duży meeple', 'duzy meeple'],
    exactPointValues: ['3', '1', '2'],
  },

  // ——— Major 02 ———
  {
    id: 'm02-goods',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Towary',
    whenScores:
      'Żetony bierze kończący miasto (nawet bez rycerza). Punkty za większość rodzajów — na końcu gry.',
    scoring:
      'Na końcu: 10 punktów za większość każdego rodzaju towaru; remis = każdy z remisujących dostaje 10.',
    conditions: 'Najpierw punktuje większość miasta, potem rozdanie żetonów towarów.',
    exceptions: 'Żetony same w sobie nie dają punktów w trakcie gry.',
    majorityRule: 'Remis większości towaru = 10 punktów dla każdego remisującego.',
    returnsMeeple: 'N/A — żetony towarów nie są meeplami.',
    moduleId: 'major-02-traders-builders',
    category: 'major',
    timing: 'end-game',
    sourceStatus: 'verified',
    verified: true,
    order: 200,
    keywords: ['towary', 'kupcy'],
    exactPointValues: ['10'],
  },
  {
    id: 'm02-pig',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Świnia',
    whenScores:
      'W trakcie gry po ukończeniu miasta z co najmniej 1 herbem, jeśli masz świnię na polu stykającym się z tym miastem.',
    scoring:
      'Każdy gracz ze świnią na polu dostaje 3 punkty × liczbę ukończonych miast przy polu świni. Świnia wraca; opcjonalnie zabierz chłopa.',
    conditions: 'Miasto musi mieć co najmniej 1 herb; świnia na polu stykającym się z tym miastem.',
    exceptions:
      'C3.1: świnia nie punktuje na końcu gry (w odróżnieniu od starych wydań).',
    majorityRule: 'N/A — trigger świni, nie majority chłopów.',
    returnsMeeple: 'Tak — świnia wraca; chłopa możesz zabrać opcjonalnie.',
    moduleId: 'major-02-traders-builders',
    category: 'major',
    timing: 'mid-game',
    sourceStatus: 'verified',
    verified: true,
    order: 210,
    keywords: ['świnia', 'swinia', 'pig'],
    exactPointValues: ['3'],
  },

  // ——— Major 03 ———
  {
    id: 'm03-fairy',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Wróżka',
    whenScores:
      'Przy punktowaniu obszaru z meeplem, do którego przypisano wróżkę (+ ochrona przed smokiem).',
    scoring: '+3 punkty przy punktowaniu tego meepla/obszaru.',
    conditions:
      'Po ukończeniu drogi/miasta bez własnych punktów możesz przypisać wróżkę do swojego meepla.',
    exceptions: 'Smok nie zjada meepla z wróżką.',
    majorityRule: 'N/A — premia do scoringu powiązanego meepla.',
    returnsMeeple: 'Wróżka pozostaje przy meeplu według zasad dodatku; meeple wraca jak zwykle po scoringu obszaru.',
    moduleId: 'major-03-dragon-fairy',
    category: 'major',
    timing: 'mid-game',
    sourceStatus: 'verified',
    verified: true,
    order: 300,
    keywords: ['wróżka', 'wrozka', 'fairy', 'smok'],
    exactPointValues: ['3'],
  },

  // ——— Major 04 ———
  {
    id: 'm04-guard',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Strażnik (wieża)',
    whenScores:
      'W trakcie gry przy zmianie strażnika; na końcu gry za strażników na wieżach.',
    scoring:
      'Zmiana strażnika: poprzedni dostaje +2 punkty. Na końcu: 5 punktów za strażnika (+ ewentualnie skradziony kafelek).',
    conditions: 'Zasięg wieży = liczba pięter (max 5).',
    exceptions: 'Bez porywania meepli (C3.1).',
    majorityRule: 'N/A — scoring strażnika wieży.',
    returnsMeeple: 'Strażnik wraca przy zmianie / według zasad wieży; na końcu po punktowaniu.',
    moduleId: 'major-04-towers-thieves',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 400,
    keywords: ['strażnik', 'straznik', 'wieża', 'wieza'],
    exactPointValues: ['2', '5'],
  },

  // ——— Major 05 ———
  {
    id: 'm05-scarecrow',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Strach na wróble',
    whenScores:
      'Własny trigger C3.1 na polu (nie mylić z końcowym scoringiem chłopów).',
    scoring:
      'Zgodnie z zasadami C3.1 dla stracha na wróble — osobny trigger, nie zastępuje 3/miasto chłopa.',
    conditions: 'Nie stawiaj chłopa na pole ze strachem.',
    exceptions:
      'Burmistrz: siła 0 w mieście; favor przy 4 sąsiadach. Kurier C3.1 ma własne reguły przenosin po scoringu drogi.',
    majorityRule: 'Burmistrz siła 0; nie mylić z końcową większością chłopów.',
    returnsMeeple: 'Według triggera stracha / scoringu powiązanego obszaru (C3.1).',
    moduleId: 'major-05-messengers-mayors',
    category: 'major',
    timing: 'special',
    sourceStatus: 'verified',
    verified: true,
    order: 500,
    keywords: ['strach', 'burmistrz', 'kurier'],
    exactPointValues: ['3'],
  },

  // ——— Major 06 ———
  {
    id: 'm06-crests',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Godła (turnieje)',
    whenScores: 'Na końcu gry za godła umieszczone na posągach nagród.',
    scoring:
      'Każde godło na posągu = 3 × liczba herbów w tym mieście (ukończone lub nie).',
    conditions: 'Godło musi leżeć na posągu nagrody.',
    exceptions: 'Nagrody turniejowe rozdawane w trakcie według nominacji 1./2. miejsca.',
    majorityRule: 'N/A — scoring godła, nie majority meepli.',
    returnsMeeple: 'N/A — godła/posągi nie są meeplami gracza.',
    moduleId: 'major-06-jousts-crests',
    category: 'major',
    timing: 'end-game',
    sourceStatus: 'verified',
    verified: true,
    order: 600,
    keywords: ['godło', 'godlo', 'turniej', 'posąg'],
    exactPointValues: ['3'],
  },

  // ——— Major 07 ———
  {
    id: 'm07-counterattack',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Kontratak (oblężenie)',
    whenScores:
      'Gdy zajęte zostanie ostatnie wolne miejsce w rzędzie stanowiska obrony.',
    scoring: '3 punkty × własne meeple na stanowisku; potem meeple wracają.',
    conditions: 'Ostatnie wolne miejsce w rzędzie stanowiska obrony.',
    exceptions: 'Twierdza liczy się jak miasto (kafelek = 6 sąsiadów).',
    majorityRule: 'Punkty × własne meeple na stanowisku (nie klasyczna majority obszaru).',
    returnsMeeple: 'Tak — po kontrataku meeple wracają.',
    moduleId: 'major-07-siege-defense',
    category: 'major',
    timing: 'mid-game',
    sourceStatus: 'verified',
    verified: true,
    order: 700,
    keywords: ['kontratak', 'oblężenie', 'oblezienie', 'katapulta'],
    exactPointValues: ['3'],
  },

  // ——— Major 08 ———
  {
    id: 'm08-bridge-mod',
    gameId: GAME_ID,
    kind: 'modifier',
    element: 'Most',
    whenScores: '',
    scoring: '',
    conditions: '',
    exceptions:
      'Most: ukończona droga 3 pkt za kafelek z mostem (zamiast 1); na końcu most = 1.',
    majorityRule: '',
    returnsMeeple: '',
    moduleId: 'major-08-castles-bridges',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    attachesToRuleId: 'base-road',
    order: 800,
    keywords: ['most'],
    exactPointValues: ['3', '1'],
  },
  {
    id: 'm08-bridge',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Most',
    whenScores: 'Przy punktowaniu drogi zawierającej most.',
    scoring:
      'Ukończona droga: 3 punkty za kafelek z mostem (zamiast 1). Na końcu gry most liczy się jako 1.',
    conditions: 'Max 1 budynek (most/zamek/stodoła) na turę.',
    exceptions: '—',
    majorityRule: 'Jak droga podstawowa.',
    returnsMeeple: 'Jak droga.',
    moduleId: 'major-08-castles-bridges',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 801,
    keywords: ['most', 'bridge'],
    exactPointValues: ['3', '1'],
  },
  {
    id: 'm08-barn',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Stodoła',
    whenScores: 'Na końcu gry (farmerzy na stodole).',
    scoring:
      '1 punkt × każdy farmer (chłop) nadal na planszy. Farmerzy na stodole pozostają farmerami.',
    conditions:
      'Stodoła wymaga 4 narożników tego samego pola + Twojego chłopa.',
    exceptions:
      'Zamek z miasta 2-segmentowego: bez scoringu miasta pod zamkiem.',
    majorityRule: 'N/A — scoring stodoły według farmerów na planszy.',
    returnsMeeple: 'Farmerzy na stodole zostają do scoringu końcowego.',
    moduleId: 'major-08-castles-bridges',
    category: 'major',
    timing: 'end-game',
    sourceStatus: 'verified',
    verified: true,
    order: 810,
    keywords: ['stodoła', 'stodola', 'zamek'],
    exactPointValues: ['1'],
  },

  // ——— Major 09 ———
  {
    id: 'm09-sheep',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Pasterze i owce',
    whenScores:
      'W trakcie gry przy wypasie (dobrowolnym lub obowiązkowym na otoczonym polu). Wilk: 0 punktów.',
    scoring:
      'Każdy pasterz na polu dostaje pełną liczbę owiec ze żetonów. Aktywny gracz +1 za każdy kafelek z gęsiami przy pasterzu (ortog./ukośnie).',
    conditions: 'Wypas na polu z pasterzem i żetonami owiec.',
    exceptions:
      'Wilk: wszystkie żetony i pasterze na połączonym polu wracają; 0 pkt. Owce/gęsi nie punktują na końcu, jeśli nie wypasione.',
    majorityRule: 'Każdy pasterz dostaje pełną liczbę owiec (nie klasyczna majority).',
    returnsMeeple: 'Tak — po wypasie / przy wilku pasterze wracają.',
    moduleId: 'major-09-sheep-shepherds',
    category: 'major',
    timing: 'mid-game',
    sourceStatus: 'verified',
    verified: true,
    order: 900,
    keywords: ['pasterz', 'owce', 'wilk', 'gęsi', 'gesi'],
    exactPointValues: ['1', '0'],
  },
  {
    id: 'm09-vineyard-mod',
    gameId: GAME_ID,
    kind: 'modifier',
    element: 'Winnica',
    whenScores: '',
    scoring: '',
    conditions: '',
    exceptions:
      'Winnica: +3 przy punktowaniu klasztoru/ogrodu za każdy sąsiadujący kafelek winnicy (ortog./ukośnie). Same nie punktują na końcu.',
    majorityRule: '',
    returnsMeeple: '',
    moduleId: 'major-09-sheep-shepherds',
    category: 'major',
    timing: 'mid-game',
    sourceStatus: 'verified',
    verified: true,
    attachesToRuleId: 'base-monastery',
    order: 910,
    keywords: ['winnica'],
    exactPointValues: ['3'],
  },
  {
    id: 'm09-vineyard',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Winnica',
    whenScores: 'Przy punktowaniu klasztoru lub ogrodu sąsiadującego z winnicą.',
    scoring:
      '+3 punkty za każdy kafelek winnicy sąsiadujący ortogonalnie lub ukośnie z klasztorem/ogrodem.',
    conditions: 'Sąsiedztwo ortogonalne lub ukośne z punktowanym klasztorem/ogrodem.',
    exceptions: 'Winnice same nie punktują na końcu gry.',
    majorityRule: 'N/A — premia do scoringu klasztoru/ogrodu.',
    returnsMeeple: 'N/A — winnica nie jest meeplem.',
    moduleId: 'major-09-sheep-shepherds',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 911,
    keywords: ['winnica'],
    exactPointValues: ['3'],
  },

  // ——— Major 10 ———
  {
    id: 'm10-circus',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Cyrk (występ)',
    whenScores:
      'Po dołożeniu placu cyrkowego — odkryty poprzedni żeton występu punktuje (meeple nie wracają).',
    scoring:
      'Każdy gracz dostaje wartość żetonu za każdego swojego meepla na kafelku występu lub jego 8 sąsiadach — zgodnie z wartością nadrukowaną na żetonie występu.',
    conditions: 'Najpierw zwykłe punktowanie ukończeń, potem żeton występu i namiot.',
    exceptions: 'Meeple nie wracają przy scoringu występu.',
    majorityRule: 'N/A — punkty × własni meeple w zasięgu występu.',
    returnsMeeple: 'Nie — przy występie meeple zostają.',
    moduleId: 'major-10-circus-artists',
    category: 'major',
    timing: 'mid-game',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 1000,
    keywords: ['cyrk', 'występ', 'wystep', 'namiot'],
    exactPointValues: [],
  },
  {
    id: 'm10-acrobats',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Akrobaci (piramida)',
    whenScores:
      'W trakcie: zamiast stawiania figury możesz zapunktować pełną piramidę z Twoim akrobatą. Na końcu: każdy akrobata.',
    scoring: '5 punktów za każdego akrobata (w trakcie przy pełnej piramidzie i na końcu gry).',
    conditions: 'Do 3 akrobatów na placu.',
    exceptions:
      'Piramida nie punktuje automatycznie przy dołożeniu trzeciego — trzeba wybrać scoring zamiast stawiania.',
    majorityRule: 'N/A — 5 pkt za każdego własnego akrobata.',
    returnsMeeple: 'Tak — po scoringu piramidy / na końcu po punktowaniu akrobatów.',
    moduleId: 'major-10-circus-artists',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 1010,
    keywords: ['akrobata', 'piramida'],
    exactPointValues: ['5'],
  },
  {
    id: 'm10-ringmistress-mod',
    gameId: GAME_ID,
    kind: 'modifier',
    element: 'Dyrektorka cyrku',
    whenScores: '',
    scoring: '',
    conditions: '',
    exceptions:
      'Dyrektorka: przy punktowaniu jej obszaru +2 za każdy plac cyrkowy/akrobatyczny na jej kafelku i 8 sąsiadach.',
    majorityRule: '',
    returnsMeeple: '',
    moduleId: 'major-10-circus-artists',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    attachesToRuleId: 'm10-circus',
    order: 1020,
    keywords: ['dyrektorka'],
    exactPointValues: ['2'],
  },
  {
    id: 'm10-ringmistress',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Dyrektorka cyrku',
    whenScores: 'Przy punktowaniu obszaru z dyrektorką (droga/miasto/klasztor/pole).',
    scoring:
      'Siła 1. +2 punkty za każdy plac cyrkowy lub akrobatyczny na jej kafelku i ośmiu sąsiadach.',
    conditions: 'Dyrektorka stoi na punktowanym obszarze.',
    exceptions: '—',
    majorityRule: 'Siła 1.',
    returnsMeeple: 'Tak — po scoringu obszaru jak zwykły meeple.',
    moduleId: 'major-10-circus-artists',
    category: 'major',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 1021,
    keywords: ['dyrektorka'],
    exactPointValues: ['1', '2'],
  },

  // ——— Minis ———
  {
    id: 'mini-flying',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Maszyny latające',
    whenScores:
      'Po udanym lądowaniu — natychmiast jak zwykłe punktowanie ukończeń. Same maszyny nie dają osobnych punktów.',
    scoring:
      'Figury umieszczone lotem punktują dokładnie jak zwykłe meeple danego typu. Na końcu gry nie ma osobnego punktowania maszyn.',
    conditions: 'Lot po linii strzałki na odległość z kości; nie ląduje na polu.',
    exceptions:
      'Nieudany lot: meeple wraca do zapasu; w tej turze nie stawiasz innego. Ukończone obszary i pola nie są legalnym celem lądowania.',
    majorityRule: 'Jak typ obszaru, na którym wylądował meeple.',
    returnsMeeple: 'Nieudany lot: tak, od razu. Udany: jak zwykły meeple po scoringu obszaru.',
    moduleId: 'mini-01-flying-machines',
    category: 'mini',
    timing: 'none',
    sourceStatus: 'no-scoring-impact',
    verified: true,
    order: 1110,
    keywords: ['maszyna', 'lot', 'latająca', 'latajaca'],
    exactPointValues: [],
  },
  {
    id: 'mini-messages',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Wiadomości (kurierzy mini)',
    whenScores:
      'W trakcie tury, gdy znacznik punktów lub kurier sam zatrzyma się na ciemnym polu (0, 5, 10…). Na końcu: suma znacznika + kuriera.',
    scoring:
      'Rozstrzygnij wiadomość albo weź 2 punkty. Efekty wiadomości: m.in. 2 pkt/herb z Twoim rycerzem, 2 pkt/rycerz, 2 pkt/chłop, punktowanie wybranego obszaru jak na końcu — zgodnie z tekstem dobranej wiadomości.',
    conditions:
      'Co najwyżej jedna wiadomość na etap punktowania, nawet jeśli oba znaczniki wpadną na ciemne pola.',
    exceptions:
      'Na końcu zsumuj znacznik i kuriera przed punktowaniem końcowym; wtedy nie dobiera się wiadomości. Z Karczmami: niepełna droga z karczmą / miasto z katedrą przy wiadomości = 0 za ten obiekt.',
    majorityRule: 'Zależy od efektu wiadomości; duży meeple liczy się jako 1 przy wiadomościach liczących figury.',
    returnsMeeple: 'Kurier zbierany przy sumowaniu na końcu; wiadomości nie zdejmują meepli automatycznie.',
    moduleId: 'mini-02-messengers',
    category: 'mini',
    timing: 'both',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 1120,
    keywords: ['wiadomość', 'wiadomosc', 'kurier', 'mini'],
    exactPointValues: ['2'],
  },
  {
    id: 'mini-ferries',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Promy',
    whenScores:
      'Po wystawieniu lub przestawieniu promu — punktuj ukończone drogi jak zwykle. Same promy nie dają osobnych punktów.',
    scoring:
      'Droga z promem bez karczmy: 1 pkt/kafelek jak baza. Prom wyznacza, które odcinki tworzą jedną drogę.',
    conditions:
      'Prom łączy dokładnie dwa odcinki na jeziorze; pozostałe odcinki kończą się.',
    exceptions:
      'Przestawienie promu może ponownie otworzyć wcześniej ukończoną drogę. Budowniczy: samo przestawienie promu nie daje podwójnej tury.',
    majorityRule: 'Jak droga podstawowa (po ustaleniu topologii przez prom).',
    returnsMeeple: 'Jak droga; prom zostaje na jeziorze (można przestawiać).',
    moduleId: 'mini-03-ferries',
    category: 'mini',
    timing: 'both',
    sourceStatus: 'no-scoring-impact',
    verified: true,
    order: 1130,
    keywords: ['prom', 'promy', 'ferry', 'jezioro'],
    exactPointValues: ['1'],
  },
  {
    id: 'mini-gold',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Kopalnie złota',
    whenScores:
      'Sztabki zbierasz przy ukończeniu obszaru. Punkty ze złota — na końcu gry (przed zwykłym końcem).',
    scoring:
      '1–3 sztabki → 1 pkt/sztabka; 4–6 → 2; 7–9 → 3; 10+ → 4. Przykład: 7 sztabek = 21 pkt; 4 = 8; 10 = 40.',
    conditions:
      'Przy scoringu obszaru rozdaj złoto z kafelka i z kafelków obszaru (klasztor: + 8 sąsiadów).',
    exceptions:
      'Chłopi nigdy nie dostają złota. Nieodebrane sztabki zdejmij przed punktowaniem końcowym.',
    majorityRule: 'N/A — tabela sztabek, nie majority.',
    returnsMeeple: 'N/A — sztabki nie są meeplami.',
    moduleId: 'mini-04-goldmines',
    category: 'mini',
    timing: 'end-game',
    sourceStatus: 'verified',
    verified: true,
    order: 1140,
    keywords: ['złoto', 'zloto', 'sztabka', 'kopalnia'],
    exactPointValues: ['1', '2', '3', '4', '10'],
  },
  {
    id: 'mini-mage-mod-road',
    gameId: GAME_ID,
    kind: 'modifier',
    element: 'Mag i Wiedźma',
    whenScores: '',
    scoring: '',
    conditions: '',
    exceptions:
      'Mag: +1/kafelek obszaru (bez herbów). Wiedźma: punkty ÷2 w górę. Najpierw karczma/katedra, potem połowa z Wiedźmą.',
    majorityRule: '',
    returnsMeeple: '',
    moduleId: 'mini-05-mage-witch',
    category: 'mini',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    attachesToRuleId: 'base-road',
    order: 1150,
    keywords: ['mag', 'wiedźma', 'wiedzma'],
    exactPointValues: ['1'],
  },
  {
    id: 'mini-mage-mod-city',
    gameId: GAME_ID,
    kind: 'modifier',
    element: 'Mag i Wiedźma',
    whenScores: '',
    scoring: '',
    conditions: '',
    exceptions:
      'Mag: +1/kafelek miasta (bez herbów). Wiedźma: punkty ÷2 w górę. Najpierw katedra, potem połowa.',
    majorityRule: '',
    returnsMeeple: '',
    moduleId: 'mini-05-mage-witch',
    category: 'mini',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    attachesToRuleId: 'base-city',
    order: 1151,
    keywords: ['mag', 'wiedźma'],
    exactPointValues: ['1'],
  },
  {
    id: 'mini-mage',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Mag i Wiedźma',
    whenScores: 'Przy punktowaniu miasta lub drogi z figurą magiczną.',
    scoring:
      'Mag: scorowany obszar +1 punkt za każdy kafelek (bez herbów). Wiedźma: punkty obszaru dzielone na pół i zaokrąglane w górę.',
    conditions: 'Figura magiczna stoi na punktowanym mieście lub drodze.',
    exceptions:
      'Przy karczmie/katedrze najpierw dolicz premię, potem połowę z Wiedźmą. Po scoringu figurę zdejmij.',
    majorityRule: 'N/A — modyfikator wartości obszaru, nie majority.',
    returnsMeeple: 'Tak — po zmodyfikowaniu wyniku figurę magiczną zdejmij na bok.',
    moduleId: 'mini-05-mage-witch',
    category: 'mini',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 1152,
    keywords: ['mag', 'wiedźma', 'wiedzma'],
    exactPointValues: ['1'],
  },
  {
    id: 'mini-robbers',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Bandyci',
    whenScores:
      'Gdy śledzony gracz zdobywa punkty; na końcu przed punktowaniem końcowym za bandytę nadal na torze.',
    scoring:
      'Połowa punktów ofiary zaokrąglona w górę (ofiara zachowuje pełne punkty). Na końcu: 3 punkty za bandytę na torze, potem zbierz go.',
    conditions: 'Bandyta stoi na polu znacznika śledzonego gracza.',
    exceptions:
      'Honor wśród złodziei: punkty z kradzieży bandyty nie triggerują innych bandytów na tym polu.',
    majorityRule: 'N/A — kradzież z toru punktacji.',
    returnsMeeple: 'Tak — bandyta wraca do zapasu po kradzieży / po końcowych 3 pkt.',
    moduleId: 'mini-06-robbers',
    category: 'mini',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 1160,
    keywords: ['bandyta', 'bandyci', 'złodziej'],
    exactPointValues: ['3'],
  },
  {
    id: 'mini-crops',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Kręgi w zbożu',
    whenScores:
      'Akcja kręgu sama nie przyznaje punktów — wykonuje się po etapie punktowania ukończeń.',
    scoring:
      '0 z samej akcji kręgu. Punktowanie obszarów = standardowe zasady; kręgi nie dają osobnej premii końcowej.',
    conditions:
      'Punktowanie obszarów w etapie 3 przed akcją kręgu. Akcja A/B według typu figur z kafelka.',
    exceptions:
      'Nie wykonuj akcji kręgu przed punktowaniem. Nie dokładaj figury do obszaru bez własnej figury tego typu.',
    majorityRule: 'N/A — akcja zmienia obecność figur, nie majority scoringu.',
    returnsMeeple: 'Zależnie od wybranej akcji A/B (dokładanie lub zdejmowanie figur).',
    moduleId: 'mini-07-crop-circles',
    category: 'mini',
    timing: 'none',
    sourceStatus: 'no-scoring-impact',
    verified: true,
    order: 1170,
    keywords: ['kręgi', 'kregi', 'zboże', 'zboze', 'crop'],
    exactPointValues: ['0'],
  },

  // ——— Mgła nad Carcassonne ———
  {
    id: 'mists-choice',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Mgła — punkty albo duchy',
    whenScores:
      'W kooperacji: gdy dołożenie wywołuje punktowanie ukończonej drogi, miasta lub (od poz. 2) zamku.',
    scoring:
      'Wybór A: punkty jak zwykle na wspólnym torze (cel poziomu 1 = 50). Wybór B: 0 punktów za to punktowanie, usuń do 3 duchów z jednego kafelka.',
    conditions: 'Tryb kooperacyjny Mgły; ukończenie drogi/miasta/(zamku od poz. 2).',
    exceptions:
      'Brak punktowania końcowego nieukończonych obiektów i pól. Kafelek startowy = 4 kafelki przy liczeniu obiektów.',
    majorityRule: 'W kooperacji scoring jest wspólny (ew. joint scoring kolorów).',
    returnsMeeple: 'Tak przy wyborze A — zdejmij punktujące meeple; przy B też zdejmij po usunięciu duchów według procedur.',
    moduleId: 'mists-over-carcassonne',
    category: 'standalone',
    timing: 'mid-game',
    sourceStatus: 'verified',
    verified: true,
    order: 2000,
    keywords: ['mgła', 'mgla', 'duchy', 'kooperacja'],
    exactPointValues: ['3', '50', '0'],
  },
  {
    id: 'mists-classic',
    gameId: GAME_ID,
    kind: 'row',
    element: 'Duchy / zamki / cmentarze (klasycznie)',
    whenScores:
      'W wariancie classic-expansion: przy punktowaniu według zasad podstawy C3 z elementami Mgły.',
    scoring:
      'Obowiązuje punktowanie podstawy (drogi, miasta, klasztory, pola); reguły mgły/duchów/zamków/cmentarzy jak w dodatku klasycznym — nie mieszaj z procedurą kooperacji.',
    conditions: 'Włącz tryb „dodatek klasyczny” w konfiguracji.',
    exceptions:
      '−2 pkt / duch przy scoringu meepla (procedury klasycznego dodatku). Strażnicy cmentarza mają własne reguły.',
    majorityRule: 'Jak baza C3 w trybie classic-expansion.',
    returnsMeeple: 'Jak baza + reguły duchów/cmentarzy dodatku klasycznego.',
    moduleId: 'mists-over-carcassonne',
    category: 'standalone',
    timing: 'both',
    sourceStatus: 'verified',
    verified: true,
    order: 2010,
    keywords: ['mgła', 'classic', 'cmentarz', 'zamek'],
    exactPointValues: [],
  },
];

const EXACT_POINTS_REQUIRED_GLOBALLY = [
  '1',
  '2',
  '3',
  '5',
  '9',
  '10',
] as const;

export function getScoringRuleById(id: string): ScoringRule | undefined {
  return carcassonneScoringRules.find((r) => r.id === id);
}

/** Modules that contribute at least one scoring rule (row or modifier). */
export function getScoringModuleIds(): CarcassonneModuleId[] {
  return Array.from(new Set(carcassonneScoringRules.map((r) => r.moduleId)));
}

export function filterScoringRulesForModules(
  enabledModuleIds: CarcassonneModuleId[]
): ScoringRule[] {
  const enabled = new Set(enabledModuleIds);
  // Base is always present in contextual tables.
  enabled.add('base');
  return carcassonneScoringRules.filter((r) => enabled.has(r.moduleId));
}

export type BuildScoringTableOptions = {
  /**
   * When true, include every ready module that has scoring rules
   * (optional “show all” mode). Default: only enabled + base.
   */
  showAll?: boolean;
  /** Optional filter by mid-game / end-game timing bucket. */
  timingFilter?: ScoringTimingKind | 'all';
};

function joinConditionsExceptions(
  conditions: string,
  exceptions: string
): string {
  const parts = [conditions, exceptions]
    .map((s) => s.trim())
    .filter((s) => s && s !== '—');
  return parts.length ? parts.join(' ') : '—';
}

/**
 * Build one shared scoring table for the current configuration.
 * Base rows first; expansion modifiers merge into conditions/exceptions.
 */
export function buildScoringTable(
  enabledModuleIds: CarcassonneModuleId[],
  options: BuildScoringTableOptions = {}
): ScoringTableRow[] {
  const moduleIds = options.showAll
    ? (ALL_MODULE_IDS.filter(
        (id) => getModuleById(id)?.status === 'ready'
      ) as CarcassonneModuleId[])
    : enabledModuleIds;

  const active = filterScoringRulesForModules(moduleIds);
  const timingFilter = options.timingFilter ?? 'all';

  const rows = active
    .filter((r) => r.kind === 'row')
    .filter((r) => timingFilter === 'all' || r.timing === timingFilter)
    .slice()
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));

  const modifiers = active.filter((r) => r.kind === 'modifier');

  return rows.map((row) => {
    const attached = modifiers
      .filter((m) => m.attachesToRuleId === row.id)
      .sort((a, b) => a.order - b.order);

    const condParts: string[] = [];
    const own = joinConditionsExceptions(row.conditions, row.exceptions);
    if (own !== '—') condParts.push(own);
    for (const m of attached) {
      const merged = joinConditionsExceptions(m.conditions, m.exceptions);
      if (merged !== '—') condParts.push(merged);
    }

    const mod = getModuleById(row.moduleId);
    return {
      id: row.id,
      element: row.element,
      whenScores: row.whenScores,
      scoring: row.scoring,
      conditionsAndExceptions: condParts.length ? condParts.join(' ') : '—',
      majorityRule: row.majorityRule,
      returnsMeeple: row.returnsMeeple,
      timing: row.timing,
      moduleId: row.moduleId,
      moduleShortName: mod?.shortName ?? row.moduleId,
      category: row.category,
      sourceStatus: row.sourceStatus,
      verified: row.verified,
      keywords: [
        ...(row.keywords ?? []),
        ...attached.flatMap((m) => m.keywords ?? []),
      ],
    };
  });
}

export type ScoringValidationIssue = string;

export function validateScoringCatalogue(
  enabledModuleIds?: CarcassonneModuleId[]
): ScoringValidationIssue[] {
  const issues: string[] = [];
  const seenIds = new Set<string>();
  const signatureSeen = new Set<string>();

  for (const rule of carcassonneScoringRules) {
    if (rule.gameId !== 'carcassonne') {
      issues.push(`Scoring rule ${rule.id} has unexpected gameId ${rule.gameId}`);
    }
    if (seenIds.has(rule.id)) {
      issues.push(`Duplicate scoring rule id: ${rule.id}`);
    }
    seenIds.add(rule.id);

    const signature = [
      rule.kind,
      rule.element,
      rule.whenScores,
      rule.scoring,
      rule.conditions,
      rule.exceptions,
      rule.moduleId,
      rule.attachesToRuleId ?? '',
    ].join('||');
    if (signatureSeen.has(signature)) {
      issues.push(`Duplicate identical scoring rule content: ${rule.id}`);
    }
    signatureSeen.add(signature);

    if (!getModuleById(rule.moduleId)) {
      issues.push(`Unknown module on scoring rule ${rule.id}: ${rule.moduleId}`);
    }

    if (rule.kind === 'modifier') {
      if (!rule.attachesToRuleId) {
        issues.push(`Modifier ${rule.id} missing attachesToRuleId`);
      } else if (!getScoringRuleById(rule.attachesToRuleId)) {
        issues.push(
          `Modifier ${rule.id} attaches to missing rule ${rule.attachesToRuleId}`
        );
      }
    }

    if (rule.kind === 'row') {
      if (!rule.whenScores.trim()) {
        issues.push(`Row ${rule.id} missing whenScores`);
      }
      if (!rule.scoring.trim()) {
        issues.push(`Row ${rule.id} missing scoring`);
      }
      if (!rule.returnsMeeple.trim()) {
        issues.push(`Row ${rule.id} missing returnsMeeple`);
      }
    }

    const blob = `${rule.whenScores} ${rule.scoring} ${rule.conditions} ${rule.exceptions} ${rule.majorityRule} ${rule.returnsMeeple}`;
    for (const value of rule.exactPointValues) {
      if (!blob.includes(value)) {
        issues.push(
          `Scoring rule ${rule.id} missing exact point value "${value}"`
        );
      }
    }
  }

  // Every ready activatable module must have at least one scoring catalogue entry.
  for (const mid of ALL_MODULE_IDS) {
    const mod = getModuleById(mid);
    if (mod?.status !== 'ready') continue;
    if (!getScoringModuleIds().includes(mid)) {
      issues.push(`Ready module ${mid} has no scoring catalogue entry`);
    }
  }

  const readyScoringModules = getScoringModuleIds().filter(
    (id) => getModuleById(id)?.status === 'ready'
  );
  const allReady = Array.from(
    new Set<CarcassonneModuleId>(['base', ...readyScoringModules])
  );

  const toCheck: CarcassonneModuleId[][] = enabledModuleIds
    ? [enabledModuleIds]
    : [
        ['base'],
        ['base', 'river', 'abbot'],
        ['base', 'major-01-inns-cathedrals'],
        [
          'base',
          'major-01-inns-cathedrals',
          'major-02-traders-builders',
          'mini-04-goldmines',
        ],
        allReady,
      ];

  for (const profile of toCheck) {
    const table = buildScoringTable(profile);
    if (table.length === 0) {
      issues.push(`Empty scoring table for profile ${profile.join('+')}`);
    }
    // Inactive expansion rows must stay hidden; base may always appear.
    for (const row of table) {
      if (row.moduleId === 'base') continue;
      if (!profile.includes(row.moduleId)) {
        issues.push(
          `Inactive module row visible: ${row.id} (${row.moduleId}) in ${profile.join('+')}`
        );
      }
    }

    const rowIds = new Set(table.map((r) => r.id));
    for (const mid of profile) {
      if (!getScoringModuleIds().includes(mid)) continue;
      const hasRow = table.some((r) => r.moduleId === mid);
      const hasModifierOnly =
        !hasRow &&
        filterScoringRulesForModules(profile).some(
          (r) =>
            r.moduleId === mid &&
            r.kind === 'modifier' &&
            r.attachesToRuleId &&
            rowIds.has(r.attachesToRuleId)
        );
      if (!hasRow && !hasModifierOnly) {
        issues.push(
          `Active scoring module ${mid} has no table row in profile ${profile.join('+')}`
        );
      }
    }

    const tableBlob = table
      .map(
        (r) =>
          `${r.element} ${r.whenScores} ${r.scoring} ${r.conditionsAndExceptions}`
      )
      .join('\n');
    if (profile.includes('base')) {
      for (const v of ['1', '2', '3', '9'] as const) {
        if (!tableBlob.includes(v)) {
          issues.push(
            `Base scoring table missing exact value ${v} (${profile.join('+')})`
          );
        }
      }
      if (!table.some((r) => r.id === 'base-road')) {
        issues.push(`Base road missing in profile ${profile.join('+')}`);
      }
    }
  }

  const allReadyBlob = buildScoringTable(allReady)
    .map(
      (r) =>
        `${r.element} ${r.whenScores} ${r.scoring} ${r.conditionsAndExceptions}`
    )
    .join('\n');
  for (const v of EXACT_POINTS_REQUIRED_GLOBALLY) {
    if (!allReadyBlob.includes(v)) {
      issues.push(`All-ready scoring table missing exact value ${v}`);
    }
  }

  // showAll must be larger than base-only and must hide nothing required.
  const showAllTable = buildScoringTable(['base'], { showAll: true });
  const baseOnly = buildScoringTable(['base']);
  if (showAllTable.length <= baseOnly.length) {
    issues.push('showAll scoring table is not larger than base-only');
  }

  return issues;
}

export function countScoringRecordsByModule(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const rule of carcassonneScoringRules) {
    if (rule.kind !== 'row') continue;
    counts[rule.moduleId] = (counts[rule.moduleId] ?? 0) + 1;
  }
  return counts;
}

export function getComponentDependentScoringIds(): string[] {
  return carcassonneScoringRules
    .filter((r) => r.kind === 'row' && r.sourceStatus === 'component-dependent')
    .map((r) => r.id);
}
